import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { useApp } from "../../context/AppContext";
import { NAVIGATION } from "../../constants/data";
import { Phone, Mail, X, ChevronRight, ExternalLink } from "lucide-react";
import { FaLinkedinIn } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/api";

/* ─────────────────────────────────────────────
 LOGO MARK
───────────────────────────────────────────── */
const LogoMark = ({ logo, size = 40 }) => (
  <div
    className="relative flex-shrink-0 rounded-xl overflow-hidden"
    style={{ width: size, height: size }}
  >
    <img
      src={logo || "/images/agora.png"}
      alt="Agora Consulting Logo"
      className="w-full h-full object-contain"
      style={{ width: size, height: size }}
    />
  </div>
);

/* ─────────────────────────────────────────────
 DESKTOP NAV LINK
───────────────────────────────────────────── */
const NavLink = ({ item, isActive, onClick }) => {
  const lineRef = useRef(null);

  // Set initial state for active links
  useEffect(() => {
    if (lineRef.current) {
      gsap.set(lineRef.current, { scaleX: isActive ? 1 : 0 });
    }
  }, [isActive]);

  // Only handle hover effects for non-active links
  const handleEnter = () => {
    if (!isActive && lineRef.current) {
      gsap.to(lineRef.current, {
        scaleX: 1,
        duration: 0.35,
        ease: "power3.out",
      });
    }
  };

  const handleLeave = () => {
    if (!isActive && lineRef.current) {
      gsap.to(lineRef.current, {
        scaleX: 0,
        duration: 0.25,
        ease: "power3.in",
      });
    }
  };

  return (
    <Link
      to={item.href}
      onClick={onClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className={`relative text-sm font-medium tracking-wide transition-colors duration-300 pb-1 ${
        isActive
          ? "text-primary-600 font-bold pointer-events-none"
          : "text-slate-700 hover:text-primary-600"
      }`}
    >
      {item.label}
      {/* Animated underline */}
      <span
        ref={lineRef}
        className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full origin-left"
        style={{
          background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
          transform: `scaleX(${isActive ? 1 : 0})`,
        }}
      />
    </Link>
  );
};

/* ─────────────────────────────────────────────
 MOBILE NAV LINK
───────────────────────────────────────────── */
const MobileNavLink = ({ item, isActive, index, onClick }) => (
  <Link
    to={item.href}
    onClick={onClick}
    className={`mobile-nav-item flex items-center justify-between py-4 px-6
   border-b border-slate-200 group transition-colors duration-300
   ${
     isActive
       ? "text-primary-600 bg-primary-50 border-primary-200 font-bold pointer-events-none"
       : "text-slate-700 hover:text-primary-600 hover:bg-slate-50"
   }`}
    style={{ opacity: 0, x: -30 }}
  >
    <div className="flex items-center gap-4">
      <span
        className={`text-xs font-mono opacity-60 ${
          isActive ? "text-primary-600" : "text-slate-500"
        }`}
        style={{ minWidth: "2ch" }}
      >
        {String(index + 1).padStart(2, "0")}
      </span>
      <span className="text-base font-medium">{item.label}</span>
      {isActive && (
        <div className="w-2 h-2 rounded-full bg-primary-600 animate-pulse" />
      )}
    </div>
    <ChevronRight
      size={16}
      className={`transition-transform duration-300
     ${isActive ? "text-primary-600" : "text-slate-400 group-hover:translate-x-1"}`}
    />
  </Link>
);

/* ─────────────────────────────────────────────
 MAIN NAVBAR
───────────────────────────────────────────── */
const Navbar = ({ companyinfo }) => {
  const location = useLocation();
  const { isMenuOpen, toggleMenu, closeMenu } = useApp();

  const navRef = useRef(null);
  const topBarRef = useRef(null);
  const logoRef = useRef(null);
  const linksRef = useRef(null);
  const ctaRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const overlayRef = useRef(null);
  const burgerRef = useRef(null);

  const [scrollDir, setScrollDir] = useState("up");
  const [lastScrollY, setLastScrollY] = useState(0);

  // Determine active navigation item based on current pathname
  const getActiveId = () => {
    const currentPath = location.pathname;

    // Handle exact matches first
    const exactMatch = NAVIGATION.find((n) => n.href === currentPath);
    if (exactMatch) {
      return exactMatch.id;
    }

    // Handle blog post routes (e.g., /blog/some-post-slug)
    if (currentPath.startsWith("/blog/")) {
      return "blog";
    }

    // Handle portfolio detail routes (e.g., /portfolio/some-project-slug)
    if (currentPath.startsWith("/portfolio/")) {
      return "portfolio";
    }

    // Handle any other nested routes
    const pathSegments = currentPath.split("/").filter(Boolean);
    if (pathSegments.length > 0) {
      const baseRoute = `/${pathSegments[0]}`;
      const baseMatch = NAVIGATION.find((n) => n.href === baseRoute);
      if (baseMatch) {
        return baseMatch.id;
      }
    }

    // Default to home for root path
    return "home";
  };

  const activeId = getActiveId();

  /* ── Scroll behaviour (simplified) ── */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrollDir(y > lastScrollY && y > 100 ? "down" : "up");
      setLastScrollY(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastScrollY]);

  /* ── Hide/show on scroll direction (disabled - always show) ── */
  useEffect(() => {
    // Navbar always stays visible at the top
    if (!navRef.current) return;
    gsap.set(navRef.current, { yPercent: 0 });
  }, [scrollDir, isMenuOpen]);

  /* ── Entry animation (runs once) ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 3.2 });

      tl.fromTo(
        navRef.current,
        { y: -100, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
      )
        .fromTo(
          logoRef.current,
          { x: -20, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.4",
        )
        .fromTo(
          linksRef.current?.querySelectorAll("a") ?? [],
          { y: -15, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.07,
            duration: 0.5,
            ease: "power3.out",
          },
          "-=0.3",
        )
        .fromTo(
          ctaRef.current,
          { x: 20, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.4",
        );
    }, navRef);

    return () => ctx.revert();
  }, []);

  /* ── Mobile menu open/close ── */
  useEffect(() => {
    if (!mobileMenuRef.current) return;

    if (isMenuOpen) {
      // Prevent body scroll
      document.body.style.overflow = "hidden";

      const tl = gsap.timeline();
      tl.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: "power2.out" },
      )
        .fromTo(
          mobileMenuRef.current,
          { x: "100%" },
          { x: "0%", duration: 0.5, ease: "power4.out" },
          "-=0.2",
        )
        .fromTo(
          mobileMenuRef.current.querySelectorAll(".mobile-nav-item"),
          { x: 30, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            stagger: 0.07,
            duration: 0.45,
            ease: "power3.out",
          },
          "-=0.3",
        )
        .fromTo(
          mobileMenuRef.current.querySelectorAll(".mobile-footer-item"),
          { y: 15, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.06,
            duration: 0.4,
            ease: "power3.out",
          },
          "-=0.3",
        );
    } else {
      document.body.style.overflow = "";

      const tl = gsap.timeline();
      tl.to(mobileMenuRef.current, {
        x: "100%",
        duration: 0.4,
        ease: "power4.in",
      }).to(
        overlayRef.current,
        { opacity: 0, duration: 0.3, ease: "power2.in" },
        "-=0.2",
      );
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  /* ── Burger icon animation ── */
  useEffect(() => {
    if (!burgerRef.current) return;
    const lines = burgerRef.current.querySelectorAll(".burger-line");
    if (isMenuOpen) {
      gsap.to(lines[0], {
        rotate: 45,
        y: 6,
        duration: 0.3,
        ease: "power3.out",
      });
      gsap.to(lines[1], { scaleX: 0, opacity: 0, duration: 0.2 });
      gsap.to(lines[2], {
        rotate: -45,
        y: -6,
        duration: 0.3,
        ease: "power3.out",
      });
    } else {
      gsap.to(lines[0], { rotate: 0, y: 0, duration: 0.3, ease: "power3.out" });
      gsap.to(lines[1], { scaleX: 1, opacity: 1, duration: 0.3 });
      gsap.to(lines[2], { rotate: 0, y: 0, duration: 0.3, ease: "power3.out" });
    }
  }, [isMenuOpen]);

  /* ── Close on route change ── */
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  const handleNavClick = useCallback(() => closeMenu(), [closeMenu]);

  return (
    <>
      {/* ── TOP INFO BAR ── */}
      <div
        ref={topBarRef}
        className="hidden lg:flex items-center justify-between px-8 py-2 text-xs bg-slate-50 border-b border-slate-200"
      >
        <div className="flex items-center gap-6 text-blue-600">
          <a
            href={`tel:${companyinfo?.phone}`}
            className="flex items-center gap-1.5 hover:text-primary-600 transition-colors duration-300"
          >
            <Phone size={11} />
            <span>{companyinfo?.phone}</span>
          </a>
          <div className="w-px h-3 bg-slate-300" />
          <a
            href={`mailto:${companyinfo?.email}`}
            className="flex items-center gap-1.5 hover:text-primary-600 transition-colors duration-300"
          >
            <Mail size={11} />
            <span>{companyinfo?.email}</span>
          </a>
        </div>
        <div className="flex items-center gap-4 text-blue-600">
          <a
            href={companyinfo?.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 hover:text-primary-600 transition-colors duration-300"
          >
            <FaLinkedinIn size={11} />
            <span>LinkedIn</span>
          </a>
          <div className="w-px h-3 bg-slate-300" />
          <span className="text-slate-500">{companyinfo?.address}</span>
        </div>
      </div>

      {/* ── MAIN NAVBAR ── */}
      <nav
        ref={navRef}
        className="fixed left-0 right-0 top-0 z-[100] py-4 bg-white border-b border-slate-200/80 shadow-sm transition-all duration-500 will-change-transform"
      >
        <div className="container-fluid flex items-center justify-between gap-8">
          {/* Logo */}
          <Link
            ref={logoRef}
            to="/"
            onClick={handleNavClick}
            className="flex items-center gap-3 flex-shrink-0 group"
          >
            <LogoMark
              logo={`${process.env.REACT_APP_BACKEND_URL}${companyinfo?.logo}`}
              size={120}
            />
          </Link>

          {/* Desktop links */}
          <div ref={linksRef} className="hidden lg:flex items-center gap-7">
            {NAVIGATION.map((item) => (
              <NavLink
                key={item.id}
                item={item}
                isActive={activeId === item.id}
                onClick={handleNavClick}
              />
            ))}
          </div>

          {/* Desktop CTA */}
          <div ref={ctaRef} className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${companyinfo?.phone}`}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-primary-600
 transition-colors duration-300"
            >
              <div
                className="w-8 h-8 rounded-full border border-slate-300 
 flex items-center justify-center hover:border-primary-500 
 transition-colors duration-300"
              >
                <Phone size={13} />
              </div>
            </a>
            <Link
              to="/contact"
              className="relative inline-flex items-center gap-2 px-5 py-2.5
 bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold
 rounded-xl overflow-hidden transition-all duration-300
 hover:shadow-glow-primary hover:-translate-y-0.5 group"
            >
              {/* Shine effect */}
              <span
                className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%]
 transition-transform duration-700 ease-in-out
 bg-gradient-to-r from-transparent via-white/10 to-transparent"
              />
              <span>Get In Touch</span>
              <ExternalLink size={13} />
            </Link>
          </div>

          {/* Mobile burger */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              ref={burgerRef}
              onClick={toggleMenu}
              className="flex flex-col items-center justify-center
 w-10 h-10 gap-1.5 rounded-xl border border-slate-300
 hover:border-slate-400 transition-colors duration-300"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="burger-line block rounded-full bg-blue-600 transition-colors"
                  style={{
                    width: i === 1 ? "14px" : "20px",
                    height: "2px",
                    transformOrigin: "center",
                  }}
                />
              ))}
            </button>
          </div>
        </div>
      </nav>

      {/* ── MOBILE MENU OVERLAY ── */}
      <div
        ref={overlayRef}
        onClick={closeMenu}
        className={`fixed inset-0 z-[110] bg-black/20 backdrop-blur-sm
 ${isMenuOpen ? "pointer-events-auto" : "pointer-events-none"}`}
        style={{ opacity: 0 }}
        aria-hidden="true"
      />

      {/* ── MOBILE MENU PANEL ── */}
      <div
        ref={mobileMenuRef}
        className="fixed top-0 right-0 bottom-0 z-[120] w-full max-w-sm
 flex flex-col overflow-y-auto bg-white shadow-2xl
 border-l border-slate-200/80"
        style={{
          transform: "translateX(100%)",
        }}
        aria-label="Mobile navigation"
        role="dialog"
        aria-modal="true"
      >
        {/* Panel header */}
        <div
          className="flex items-center justify-between p-5
 border-b border-slate-200"
        >
          <Link
            to="/"
            onClick={handleNavClick}
            className="flex items-center gap-3"
          >
            <LogoMark
              logo={`${process.env.REACT_APP_BACKEND_URL}${companyinfo?.logo}`}
              size={100}
            />
          </Link>

          <button
            onClick={closeMenu}
            className="w-9 h-9 rounded-xl border border-slate-300
 flex items-center justify-center
 hover:border-red-400 hover:text-red-500
 text-slate-600 transition-all duration-300"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        {/* Nav links */}
        <div className="flex-1 py-4">
          {NAVIGATION.map((item, i) => (
            <MobileNavLink
              key={item.id}
              item={item}
              isActive={activeId === item.id}
              index={i}
              onClick={handleNavClick}
            />
          ))}
        </div>

        {/* Panel footer */}
        <div
          className="p-6 border-t border-slate-200 space-y-4 
 bg-slate-50"
        >
          <Link
            to="/contact"
            onClick={handleNavClick}
            className="mobile-footer-item btn-primary w-full justify-center"
          >
            <Phone size={15} />
            Get In Touch
          </Link>

          <div className="mobile-footer-item grid grid-cols-2 gap-3">
            <a
              href={`tel:${companyinfo?.phone}`}
              className="flex items-center gap-2 text-xs text-blue-600
 hover:text-primary-600 transition-colors duration-300"
            >
              <Phone size={12} />
              <span className="truncate">{companyinfo?.phone}</span>
            </a>
            <a
              href={`mailto:${companyinfo?.email}`}
              className="flex items-center gap-2 text-xs text-blue-600
 hover:text-primary-600 transition-colors duration-300"
            >
              <Mail size={12} />
              <span className="truncate">Email Us</span>
            </a>
          </div>

          <div className="mobile-footer-item flex items-center gap-2">
            <div className="h-px flex-1 bg-slate-300" />
            <a
              href={companyinfo?.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-slate-500
 hover:text-primary-600 transition-colors duration-300"
            >
              <FaLinkedinIn size={12} />
              LinkedIn
            </a>
            <div className="h-px flex-1 bg-slate-300" />
          </div>

          <p className="mobile-footer-item text-center text-xs text-slate-500">
            Addis Ababa, Ethiopia
          </p>
        </div>
      </div>
    </>
  );
};

export default Navbar;
